import Vue from "vue";
import { reflectKeys, jwt, appLocalStorage } from '@/app/shared/services';

import { fetchGuide, fetchTags, fetchArtefacts, fetchArtefact, fetchArtefactUnit, fetchMock } from '../services';

/** Initial state */
const initialState = {
  apiCallStatusTrack: {
    guide: false,
    tags: false,
    artefacts: false,
    artefact: false,
    artefactUnit: false,
    payload: false
  },
  data: null,
  error: null,
  guide: [],
  tags: [],
  artefacts: [],
  artefact: {},
  artefactUnit: {}
};

/** Prefix for mutation types and actiontypes */
const namespacedPrefix = '[PREDICTOR]';

/**
 * Mutation types
 */
const mutationTypes = reflectKeys(
  [
    'PREDICTOR_DATA_REQUEST',
    'PREDICTOR_DATA_SUCCESS',
    'PREDICTOR_DATA_ERROR',
    'PREDICTOR_DATA_RESET',
    'LOOKUP_PREDICTOR_GUIDE',
    'SET_PREDICTOR_GUIDE',
    'SET_PREDICTOR_GUIDE_ON',
    'LOOKUP_PREDICTOR_TAGS',
    'SET_PREDICTOR_TAGS',
    'LOOKUP_PREDICTOR_ARTEFACTS',
    'SET_PREDICTOR_ARTEFACTS',
    'LOOKUP_PREDICTOR_ARTEFACT',
    'SET_PREDICTOR_ARTEFACT',
    'LOOKUP_PREDICTOR_ARTEFACT_UNIT',
    'SET_PREDICTOR_ARTEFACT_UNIT',
    'LOOKUP_PREDICTOR_PAYLOAD',
    'SET_PREDICTOR_PAYLOAD',
    'RESET_PREDICTOR'
  ],
  namespacedPrefix
);

const {
  PREDICTOR_DATA_REQUEST,
  PREDICTOR_DATA_SUCCESS,
  PREDICTOR_DATA_ERROR,
  PREDICTOR_DATA_RESET,
  LOOKUP_PREDICTOR_GUIDE,
  SET_PREDICTOR_GUIDE,
  SET_PREDICTOR_GUIDE_ON,
  LOOKUP_PREDICTOR_TAGS,
  SET_PREDICTOR_TAGS,
  LOOKUP_PREDICTOR_ARTEFACTS,
  SET_PREDICTOR_ARTEFACTS,
  LOOKUP_PREDICTOR_ARTEFACT,
  SET_PREDICTOR_ARTEFACT,
  LOOKUP_PREDICTOR_ARTEFACT_UNIT,
  SET_PREDICTOR_ARTEFACT_UNIT,
  LOOKUP_PREDICTOR_PAYLOAD,
  SET_PREDICTOR_PAYLOAD,
  RESET_PREDICTOR
} = mutationTypes;

/**
 * Auth data mutations
 */
const mutations = {
  /** auth data request */
  [PREDICTOR_DATA_REQUEST](state) {
    Object.assign(state, { error: null });
  },

  /** auth data success */
  [PREDICTOR_DATA_SUCCESS](state, payload) {
    Object.assign(state, { apiCallStatusTrack: initialState.apiCallStatusTrack, data: payload, user: payload, isAuthenticated: true });
  },

  /** auth data error */
  [PREDICTOR_DATA_ERROR](state, payload) {
    Object.assign(state, {
      apiCallStatusTrack: initialState.apiCallStatusTrack,
      data: null,
      error: payload || true
    });
  },

  /** reset auth data */
  [PREDICTOR_DATA_RESET](state) {
    Object.assign(state, initialState);
  },

  [LOOKUP_PREDICTOR_GUIDE](state) {
    state.apiCallStatusTrack.guide = true;
  },
  [SET_PREDICTOR_GUIDE](state, payload) {
    state.guide = payload.res.data;
    state.apiCallStatusTrack.guide = false;
  },
  [SET_PREDICTOR_GUIDE_ON](state, guide) {
    state.guideOn = guide;
  },
  [LOOKUP_PREDICTOR_TAGS](state) {
    state.apiCallStatusTrack.tags = true;
  },
  [SET_PREDICTOR_TAGS](state, payload) {
    state.tags = payload.res.data;
    state.tagsCount = payload.res.data ? payload.res.data.length : 0;
    state.apiCallStatusTrack.tags = false;
  },
  [LOOKUP_PREDICTOR_ARTEFACTS](state) {
      state.apiCallStatusTrack.artefacts = true;
  },
  [SET_PREDICTOR_ARTEFACTS](state, payload) {
    state.artefacts = payload.artefacts;
    state.artefactsCount = payload.artefacts ? payload.artefacts.length : 0;
    state.apiCallStatusTrack.artefacts = false;
  },
  [LOOKUP_PREDICTOR_ARTEFACT](state) {
    state.apiCallStatusTrack.artefact = true;
  },
  [SET_PREDICTOR_ARTEFACT](state, payload) {
    state.artefact = {};
    state.artefact = payload;
    state.apiCallStatusTrack.artefact = false;
  },
  [LOOKUP_PREDICTOR_ARTEFACT_UNIT](state) {
    state.apiCallStatusTrack.artefactUnit = true;
  },
  [SET_PREDICTOR_ARTEFACT_UNIT](state, payload) {
    state.artefactUnit = payload;
    state.apiCallStatusTrack.artefactUnit = false;
  },
  [LOOKUP_PREDICTOR_PAYLOAD](state) {
    state.apiCallStatusTrack.payload = true;
  },
  [SET_PREDICTOR_PAYLOAD](state, payload) {
    state.artefact = payload;
    state.apiCallStatusTrack.payload = false;
  },
  [RESET_PREDICTOR](state) {
    for (let f in state) {
      Vue.set(state, f, state.initialState[f]);
    }
  }

};

/** Actions types constants */
export const actionsTypes = reflectKeys([
  'FETCH_PREDICTOR_GUIDE',
  'FETCH_PREDICTOR_TAGS',
  'FETCH_PREDICTOR_ARTEFACTS',
  'FETCH_PREDICTOR_ARTEFACT',
  'FETCH_PREDICTOR_ARTEFACT_UNIT',
  'FETCH_PREDICTOR_PAYLOAD',
  'RESET_PREDICTOR_STATE'
], namespacedPrefix);

/**
 * Users data actions
 */
const actions = {

  async [actionsTypes.FETCH_PREDICTOR_GUIDE](context, payload) {
    context.commit(LOOKUP_PREDICTOR_GUIDE);
    return await fetchGuide(payload)
      .then(data => {
        payload.res = data;
        context.commit(SET_PREDICTOR_GUIDE, payload);
      })
      .catch(error => {
        payload.res = error;
        context.commit(SET_PREDICTOR_GUIDE, payload);
      });
  },
  async [actionsTypes.FETCH_PREDICTOR_TAGS]({ commit }, payload) {
    commit(LOOKUP_PREDICTOR_TAGS);
    return await fetchTags(payload)
      .then(data => {
          payload.res = data;
          commit(SET_PREDICTOR_TAGS, payload);
      })
      .catch(error => {
          payload.res = error;
          commit(SET_PREDICTOR_TAGS, payload);
      });
  },
  async [actionsTypes.FETCH_PREDICTOR_ARTEFACTS]({ commit }, payload) {
    commit(LOOKUP_PREDICTOR_ARTEFACTS);
    return await fetchArtefacts(payload)
      .then(data => {
          payload.res = data;
          commit(SET_PREDICTOR_ARTEFACTS, payload);
      })
      .catch(error => {
          payload.res = error;
          commit(SET_PREDICTOR_ARTEFACTS, payload);
      });
  },
  async [actionsTypes.FETCH_PREDICTOR_ARTEFACT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_PREDICTOR_ARTEFACT);
    if (prevArtifact !== undefined) {
        return context.commit(SET_PREDICTOR_ARTEFACT, prevArtifact);
    }

    const promise1 = fetchMock(payload);
    const promise2 = fetchArtefact(payload);

    return await Promise.all([promise1, promise2])
      .then(values => {
        // console.log(values);
        let mock = {};
        try{
          payload.mock = JSON.parse(JSON.stringify(values[0].data));
        } catch(e){
          console.log(e);
        }
        let data = values[1];
        payload.res = data.data;
        context.commit(SET_PREDICTOR_ARTEFACT, payload);
      })
      .catch(error => {
        payload.mock = {error: 'No Mock records found for the search criteria.'};
        payload.res = {error: 'No records found for the search criteria.'};
        context.commit(SET_PREDICTOR_ARTEFACT, payload);
        // throw new Error(error);
      });
  },
  async [actionsTypes.FETCH_PREDICTOR_ARTEFACT_UNIT](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_PREDICTOR_ARTEFACT_UNIT);
    if (prevArtifact !== undefined) {
        return context.commit(SET_PREDICTOR_ARTEFACT_UNIT, prevArtifact);
    }
    return await fetchArtefactUnit(payload)
      .then(data => {
        payload.res = data.data;
        context.commit(SET_PREDICTOR_ARTEFACT_UNIT, payload);
      })
      .catch(error => {
        payload.res = { error: 'No records found for the given criteria.' };
        context.commit(SET_PREDICTOR_ARTEFACT_UNIT, payload);
      });
  },
  async [actionsTypes.FETCH_PREDICTOR_PAYLOAD](context, payload, prevArtifact) {
    // avoid extronuous network call if search exists
    context.commit(LOOKUP_PREDICTOR_PAYLOAD);
    if (prevArtifact !== undefined) {
      return context.commit(SET_PREDICTOR_PAYLOAD, prevArtifact);
    }

    context.commit(SET_PREDICTOR_PAYLOAD, payload);
    return await payload;
  },

  [actionsTypes.RESET_PREDICTOR_STATE]({ commit }) {
      commit(RESET_PREDICTOR);
  }
};

/**
 * Users data actions
 */
const state = {
  ...initialState,
  guideOn: [],
  loading: true,
  tagsCount: 0,
  artefactsCount: 0
};

const getters = {
  isLoading(state) {
    state.loading = false;
    for (let x in state.apiCallStatusTrack) {
      if(state.apiCallStatusTrack[x]) {
        state.loading = true;
        break;
      }
    }
    return state.loading;
  },
  tagsCount(state) {
    return state.tagsCount;
  },
  tags(state) {
    return state.tags;
  },
  artefactsCount(state) {
    return state.artefactsCount;
  },
  artefacts(state) {
    return state.artefacts;
  },
  artefact(state) {
    return state.artefact;
  },
  artefactUnit(state) {
    return state.artefactUnit;
  },
  guide(state) {
    return state.guide;
  },
  guideOn(state) {
    return state.guideOn;
  }
};

export default {
  mutations,
  actions,
  state,
  getters
};
